import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import CheckoutButton from '../components-library/buttons/CheckoutButton';
import CompanyInfo from '../components-library/company/CompanyInfo';
import Footer from '../components-library/footers/Footer';
import OngoingOrder from '../components-library/order/OngoingOrder';
import { SCREENS } from '../services/exports/Constants';
import { CompanyDetailsInterface, Order, OrderMethodModalProps } from '../services/exports/Interfaces';
import useScreenType from '../hooks/utility/useScreenType';
import CookieConsent from '../components-library/CookieConsent';
import CompanyDetailsModal from '../modals/CompanyDetailsModal';
import { useTranslation } from 'react-i18next';
import useInitialData from '../hooks/global/useInitialData';
import useAppState from '../hooks/global/useAppState';
import useRouter from '../hooks/navigation/useRouter';
import MenuCategoryList from '../components-library/menu/MenuCategoryList';
import OrderCarousel from '../components-library/carousel/OrderCarousel';
import OrderManager from '../services/api/OrderManager';
import useAuth from '../hooks/global/useAuth';
import collect from 'collect.js';
import PageLayout from '../components-library/layouts/PageLayout';
import useAppMethods from '../hooks/utility/useAppMethods';
import PoweredByFooter from '../components-library/footers/PoweredByFooter';
import DealsCarousel from '../components-library/carousel/DealsCarousel';
import useOrderFlow from '../hooks/order/useOrderFlow';
import SavingsBanner from '../components-library/banners/SavingsBanner';
import classnames from 'classnames';
import useDeepRef from '../hooks/utility/useDeepRef';
import useStorefront from '../hooks/storefront/useStorefront';
import OrderMethod from '../modules/storefront/components/orders/OrderMethod';
import CashbackBalance from '../components-library/cashback/CashbackBalance';
import usePageHeader from '../hooks/navigation/usePageHeader';
import AppliedOfferToast from '../features/offers/components/AppliedOfferToast';
import classNames from 'classnames';
import useIsOrderSlotValidWatcher from '../hooks/availability/useIsOrderSlotValidWatcher';

interface LayoutProps {
  isDesktop: boolean;
  menuListRef: React.RefObject<any>;
  renderLastOrder: () => ReactElement;
  company: CompanyDetailsInterface;
}

export default function RestaurantDetailsScreen() {
  const { t } = useTranslation(null, { keyPrefix: 'Pages:RestaurantDetailsScreen' });

  const { data } = useInitialData();
  const { company, franchise, loading } = data;
  const { isValidSlot, isLoading: isOrderSlotValidating } = useIsOrderSlotValidWatcher();
  const { isLoggedIn } = useAuth();
  const { isDesktop } = useScreenType();
  const { toggleOrderMethodModal } = useAppMethods();
  const { orderMethodModalProps, menuItemModalProps } = useAppState();
  const { navigate } = useRouter();
  const { isStorefront } = useStorefront();
  const { pageHeaderHeight } = usePageHeader();
  const { isOrderMethodInfoComplete, ongoingOrder } = useOrderFlow();
  const orderMethodModalPropsRef = useDeepRef<OrderMethodModalProps>(orderMethodModalProps);
  const [showCompanyDetailsModal, setShowCompanyDetailsModal] = useState(false);
  const [lastOrder, setLastOrder] = useState<Order | null>(null);
  const [loadingNavigation, setLoadingNavigation] = useState(false);

  const menuListRef = useRef(null);

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    if (
      loading ||
      isOrderSlotValidating ||
      orderMethodModalPropsRef.current.openModal ||
      (isOrderMethodInfoComplete && isValidSlot) ||
      !company?.has_live_orders
    ) {
      return;
    }

    if (!franchise?.has_multiple_companies && isValidSlot) {
      return;
    }

    const timeout = setTimeout(() => toggleOrderMethodModal({ openModal: true }), 4000);

    return () => clearTimeout(timeout);
  }, [
    loading,
    isOrderMethodInfoComplete,
    company?.has_live_orders,
    franchise?.has_multiple_companies,
    isValidSlot,
    isOrderSlotValidating,
  ]);

  async function init(): Promise<void> {
    if (!isLoggedIn) {
      return;
    }

    const { response, success } = await OrderManager.getOrderHistory({ limit: 1 }, undefined, company?.id);

    if (success) {
      setLastOrder(collect(response.data.data).first());
    }
  }

  function toggleCompanyDetailsModal(): void {
    setShowCompanyDetailsModal(!showCompanyDetailsModal);
  }

  const banner = useMemo(
    () => Boolean(ongoingOrder?.id) && <OngoingOrder className={classNames({ 'border-b': isStorefront })} />,
    [ongoingOrder?.id, isStorefront],
  );

  const renderLastOrder = (): ReactElement => {
    return (
      lastOrder && (
        <div className="w-full lg:mt-lg my-md">
          <OrderCarousel order={lastOrder} />
        </div>
      )
    );
  };

  const checkoutButton = useMemo<ReactElement | undefined>(() => {
    return (
      !orderMethodModalProps?.openModal &&
      !menuItemModalProps?.openModal &&
      !isDesktop && (
        <div className="lg:hidden block fixed bottom-0 w-full z-50 space-y-md px-md">
          <AppliedOfferToast />
          <CheckoutButton
            className="bg-transparent border-t-none !mb-4 !p-0"
            title={t('checkout_button.title')}
            onClick={() => {
              setLoadingNavigation(true);
              setTimeout(() => {
                navigate({
                  web: {
                    to: SCREENS.BASKET,
                  },
                });
              }, 500);
            }}
            loading={loadingNavigation}
            hideLoginPrompt
            hideAddProductsButton
            hideCashback
          />
        </div>
      )
    );
  }, [orderMethodModalProps, isDesktop, menuItemModalProps, loadingNavigation]);

  const renderCompanyDetailsModal = useMemo<JSX.Element>(() => {
    return (
      <CompanyDetailsModal data={company} openModal={showCompanyDetailsModal} toggleModal={toggleCompanyDetailsModal} />
    );
  }, [company, showCompanyDetailsModal]);

  const renderMainLayout = (): JSX.Element => {
    const config: LayoutProps = {
      isDesktop,
      menuListRef,
      renderLastOrder,
      company,
    };
    return isStorefront ? (
      <RestaurantDetailsScreen.Storefront {...config} />
    ) : (
      <RestaurantDetailsScreen.Default {...config} />
    );
  };

  return (
    <PageLayout
      headerClassName={classnames({
        'fixed w-full top-0 z-40': isDesktop,
      })}
      className="!shadow-none"
    >
      <div>
        <div style={{ height: isDesktop ? pageHeaderHeight.desktop : 0 }} />
        <SavingsBanner />
        <main className="h-screen w-full" data-test="restaurant-details-body">
          <div>{banner}</div>
          {renderMainLayout()}
          {checkoutButton}
          <PoweredByFooter />
        </main>
        {renderCompanyDetailsModal}
        <CookieConsent />
      </div>
    </PageLayout>
  );
}

RestaurantDetailsScreen.Storefront = ({ isDesktop, menuListRef, renderLastOrder }: LayoutProps) => {
  return (
    <div>
      <div
        className={classnames('flex flex-row lg:pt-md mt-lg gap-lg', {
          'layout-container': isDesktop,
        })}
      >
        <div className="relative flex-1 min-w-0">
          <OrderMethod className="mb-md" />
          <div className="pr-none lg:pr-md">
            <MenuCategoryList
              ref={menuListRef}
              DiscountsComponent={<DealsCarousel />}
              LastOrderComponent={renderLastOrder()}
              ListFooterComponent={<Footer />}
              ListHeaderComponent={
                <>
                  <CashbackBalance type={2} className="mb-md" />
                  <div className="w-full lg:px-none px-md">
                    <div className="w-full h-[1px] bg-[#E6E6E6] dark:bg-brand-border mb-md lg:mx-none" />
                  </div>
                </>
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

RestaurantDetailsScreen.Default = ({ isDesktop, menuListRef, renderLastOrder }: LayoutProps) => {
  return (
    <div>
      <div className="space-y-[46px]">
        <CompanyInfo />
        <div className="lg:hidden block w-full">
          <OrderMethod type={2} />
        </div>
      </div>
      <div
        className={classnames('flex flex-row lg:pt-md lg:mt-lg mt-md gap-lg', {
          'layout-container': isDesktop,
        })}
      >
        <div className="relative flex-1 min-w-0">
          <div className="pr-none lg:pr-md">
            <MenuCategoryList
              ref={menuListRef}
              ListFooterComponent={<Footer />}
              DiscountsComponent={<DealsCarousel />}
              LastOrderComponent={renderLastOrder()}
              ListHeaderComponent={
                <>
                  <CashbackBalance type={2} className="lg:mt-sm mt-none lg:mb-lg mb-md" />
                  <div className="w-full lg:px-none px-md lg:hidden block">
                    <div className="w-full h-[1px] bg-[#E6E6E6] dark:bg-brand-border mb-md lg:mx-none" />
                  </div>
                </>
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};
